import type { ReactNode } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Mission } from '../../data/missions';
import HowToUse from '../HowToUse/HowToUse';
import { Button } from '@/components/ui/button';

interface MissionShellProps {
  mission: Mission;
  onBack: () => void;
  steps: string[];
  wide?: boolean;
  backLabel?: string;
  children: ReactNode;
}

/** Card chrome shared by the newer mission panels: back button, title block, how-to. */
function MissionShell({ mission, onBack, steps, wide, backLabel = 'All missions', children }: MissionShellProps) {
  return (
    <section
      style={{ borderTopColor: mission.color }}
      className={`mx-auto rounded-2xl border border-border border-t-[6px] bg-card p-8 shadow-sm ${
        wide ? 'max-w-[900px]' : 'max-w-[700px]'
      }`}
    >
      <Button
        variant="ghost"
        onClick={onBack}
        style={{ color: mission.color }}
        className="mb-5 h-auto gap-1.5 px-2 py-1.5 text-sm font-semibold hover:bg-transparent"
      >
        <ArrowLeftIcon className="size-5" /> {backLabel}
      </Button>

      <div className="mb-6 flex items-start gap-4">
        <span
          style={{ backgroundColor: `${mission.color}26`, color: mission.color }}
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl"
        >
          <mission.icon className="h-7 w-7" />
        </span>
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">{mission.title}</h2>
          <p className="text-sm text-muted-foreground">{mission.description}</p>
        </div>
      </div>

      <HowToUse steps={steps} />
      {children}
    </section>
  );
}

export function ErrorNotice({ message }: { message: string }) {
  return <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">⚠️ {message}</div>;
}

export function SuccessNotice({ children }: { children: ReactNode }) {
  return <div className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">{children}</div>;
}

export function InfoNotice({ children }: { children: ReactNode }) {
  return <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">{children}</div>;
}

export default MissionShell;
